import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  ChevronRight, ExternalLink, Star, Check, ArrowRight, 
  HelpCircle, Zap, Users, Target, TrendingUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function SEOPageRenderer({ prefix = '' }) {
  const { slug } = useParams();
  const fullSlug = prefix ? `${prefix}/${slug}` : slug;
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch page data
  useEffect(() => {
    const fetchPage = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_URL}/api/pseo/render/${fullSlug}`);
        if (!res.ok) {
          setError('Page not found');
          return;
        }
        const data = await res.json();
        setPageData(data);
      } catch (err) {
        setError('Failed to load page');
      } finally {
        setLoading(false);
      }
    };
    
    if (fullSlug) fetchPage();
  }, [fullSlug]);

  // Set document title and SEO meta tags (must be before any returns)
  useEffect(() => {
    if (pageData) {
      const pTitle = pageData.meta_title || pageData.title || 'MarkeeMix';
      const pDesc = pageData.meta_description || '';
      document.title = pTitle;
      const metaTags = {
        'description': pDesc,
        'og:title': pTitle,
        'og:description': pDesc,
        'og:type': 'website',
        'og:url': `https://markeemix.com/${fullSlug}`,
        'twitter:card': 'summary_large_image',
        'twitter:title': pTitle,
        'twitter:description': pDesc,
      };
      Object.entries(metaTags).forEach(([key, value]) => {
        const isOg = key.startsWith('og:') || key.startsWith('twitter:');
        const attr = isOg ? 'property' : 'name';
        let el = document.querySelector(`meta[${attr}="${key}"]`);
        if (!el) { el = document.createElement('meta'); el.setAttribute(attr, key); document.head.appendChild(el); }
        el.setAttribute('content', value);
      });
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) { canonical = document.createElement('link'); canonical.setAttribute('rel', 'canonical'); document.head.appendChild(canonical); }
      canonical.setAttribute('href', `https://markeemix.com/${fullSlug}`);
    }
  }, [pageData, fullSlug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  if (error || !pageData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <h1 className="text-2xl font-bold text-slate-800 mb-2">Page Not Found</h1>
            <p className="text-slate-600 mb-4">The page you're looking for doesn't exist.</p>
            <Link to="/">
              <Button>Go Home</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { 
    title, meta_title, meta_description, h1, intro, 
    faqs, schema_json, page_type, related_pages, variables 
  } = pageData;

  const pageTitle = meta_title || title || 'MarkeeMix';
  const pageDescription = meta_description || '';

  // Benefits based on page type
  const benefits = [
    { icon: Zap, text: 'Fast & Free' },
    { icon: Target, text: 'Accurate Results' },
    { icon: Users, text: '50,000+ Users' },
    { icon: TrendingUp, text: 'Improve Rankings' },
  ];

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white">
        {/* Breadcrumbs */}
        <div className="bg-white border-b">
          <div className="max-w-6xl mx-auto px-4 py-3">
            <nav className="flex items-center text-sm text-slate-600">
              <Link to="/" className="hover:text-violet-600">Home</Link>
              <ChevronRight className="w-4 h-4 mx-2" />
              <span className="capitalize">{page_type}</span>
              <ChevronRight className="w-4 h-4 mx-2" />
              <span className="text-slate-900 font-medium">{h1}</span>
            </nav>
          </div>
        </div>

        {/* Hero Section */}
        <section className="py-16 px-4 bg-gradient-to-br from-violet-600 to-purple-700 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-4 bg-white/20 text-white border-white/30">
              Free {page_type === 'tool' ? 'Tool' : 'Resource'}
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">{h1}</h1>
            <p className="text-xl text-violet-100 mb-8 max-w-2xl mx-auto">{intro}</p>
            
            {page_type === 'tool' && (
              <div className="flex flex-wrap justify-center gap-4">
                <Link to={`/${variables?.tool_name?.toLowerCase().replace(/\s+/g, '-') || 'seo-tools'}`}>
                  <Button size="lg" className="bg-white text-violet-700 hover:bg-violet-50">
                    Try Now - It's Free <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                  View Demo
                </Button>
              </div>
            )}
            
            {page_type === 'comparison' && (
              <div className="flex justify-center gap-4 mt-6">
                <Badge className="text-lg px-4 py-2 bg-white/20">{variables?.competitor_1}</Badge>
                <span className="text-2xl font-bold">VS</span>
                <Badge className="text-lg px-4 py-2 bg-white/20">{variables?.competitor_2}</Badge>
              </div>
            )}
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-12 px-4 bg-white border-b">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-center gap-3 p-4 rounded-lg bg-slate-50">
                  <div className="p-2 bg-violet-100 rounded-lg">
                    <benefit.icon className="w-5 h-5 text-violet-600" />
                  </div>
                  <span className="font-medium text-slate-700">{benefit.text}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            {/* Features */}
            {page_type === 'tool' && (
              <Card className="mb-12">
                <CardHeader>
                  <CardTitle>Key Features</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    {['Real-time analysis', 'Export to PDF/CSV', 'No signup required', 'Mobile friendly', 'Accurate data', 'Fast results'].map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                        <Check className="w-5 h-5 text-green-600" />
                        <span className="text-slate-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* How to Use */}
            <Card className="mb-12">
              <CardHeader>
                <CardTitle>How to Use {h1}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[
                    { step: 1, title: 'Enter Your Data', desc: 'Provide the required information in the input field.' },
                    { step: 2, title: 'Click Analyze', desc: 'Our tool will process your request instantly.' },
                    { step: 3, title: 'Get Results', desc: 'View detailed insights and export if needed.' },
                  ].map((item) => (
                    <div key={item.step} className="flex gap-4 p-4 bg-slate-50 rounded-lg">
                      <div className="w-10 h-10 bg-violet-600 text-white rounded-full flex items-center justify-center font-bold">
                        {item.step}
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-800">{item.title}</h4>
                        <p className="text-slate-600">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* FAQs */}
            {faqs && faqs.length > 0 && (
              <Card className="mb-12">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <HelpCircle className="w-5 h-5" />
                    Frequently Asked Questions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {faqs.map((faq, idx) => (
                      <details key={idx} className="group p-4 bg-slate-50 rounded-lg cursor-pointer">
                        <summary className="font-semibold text-slate-800 list-none flex justify-between items-center">
                          {faq.question}
                          <ChevronRight className="w-5 h-5 group-open:rotate-90 transition-transform" />
                        </summary>
                        <p className="mt-3 text-slate-600 pl-4 border-l-2 border-violet-300">
                          {faq.answer}
                        </p>
                      </details>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </section>

        {/* Related Pages */}
        {related_pages && related_pages.length > 0 && (
          <section className="py-12 px-4 bg-slate-50">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-2xl font-bold text-slate-800 mb-6">Related Tools & Resources</h2>
              <div className="grid md:grid-cols-3 gap-6">
                {related_pages.map((page, idx) => (
                  <Link key={idx} to={`/${page.slug}`}>
                    <Card className="h-full hover:shadow-lg transition-shadow">
                      <CardContent className="p-6">
                        <h3 className="font-semibold text-slate-800 mb-2">{page.title}</h3>
                        <p className="text-sm text-slate-600 line-clamp-2">{page.meta_description}</p>
                        <div className="mt-4 flex items-center text-violet-600 text-sm font-medium">
                          Learn more <ArrowRight className="w-4 h-4 ml-1" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA Section */}
        <section className="py-16 px-4 bg-gradient-to-br from-violet-600 to-purple-700 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
            <p className="text-violet-100 mb-8">
              Join 50,000+ marketers using MarkeeMix to improve their SEO.
            </p>
            <div className="flex justify-center gap-4">
              <Link to="/register">
                <Button size="lg" className="bg-white text-violet-700 hover:bg-violet-50">
                  Start Free Trial <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                  View Pricing
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
